import {Random} from "@dp-instant/utils";
import {TypesGame} from "@game/TypesGame";
import {inject, injectable} from "inversify";
import {CookieModel} from "@game/coregame/model/CookieModel";
import {PlayerType} from "../interfaces/PlayerType";

import {
    GameColors,
    GiftType,
    ISelectableBuilding,
    ISelectableColor,
    ISelectableFighter,
    PlayerColor,
    SelectableBuildingDataSet,
    SelectableColorsDataSet,
    SelectableFighterDataSet,
    SkinType,
    UserSelectableColorsSet
} from "@game/coregame/settings";

@injectable()
export class SkinManager {
    @inject(TypesGame.cookieModel)
    private readonly cookies: CookieModel;
    private readonly fighters: Map<PlayerType, string> = new Map();
    private readonly buildings: Map<PlayerType, string> = new Map();

    private fillSkins(): void {
        const level: number = this.cookies.absoluteLevelNum;

        const defaultBuilding = SelectableBuildingDataSet[0];
        const defaultFighter = SelectableFighterDataSet[0];

        const [selectedFighter] = SelectableFighterDataSet.filter(
            item => item.id === this.cookies.selected_fighter_id
        );
        const [selectedBuilding] = SelectableBuildingDataSet.filter(
            item => item.id === this.cookies.selected_building_id
        );
        this.fighters.set(PlayerType.First, (selectedFighter || defaultFighter).textureUrl);
        this.buildings.set(PlayerType.First, (selectedBuilding || defaultBuilding).textureUrl);
        this.buildings.set(PlayerType.Neutral, defaultBuilding.textureUrl);
        Object.keys(PlayerType).forEach((key, type) => {
            if (type == PlayerType.First || type === PlayerType.Neutral) {
                return;
            }
            if (level <= 5) {
                this.fighters.set(type, defaultFighter.textureUrl);
                this.buildings.set(type, defaultBuilding.textureUrl);
            } else {
                this.fighters.set(type, Random.from(SelectableFighterDataSet).textureUrl);
                this.buildings.set(type, Random.from(SelectableBuildingDataSet).textureUrl);
            }
        });
    }

    private clearSkins(): void {
        this.fighters.clear();
        this.buildings.clear();
    }

    public getGift(): GiftType | undefined {
        const level = this.cookies.absoluteLevelNum;
        const fighterSet = this.cookies.getUserFighterSet();
        const buildingSet = this.cookies.getUserBuildingSet();
        const items = SelectableFighterDataSet.concat(SelectableBuildingDataSet).filter(item => {
            if (item.playerLevelUnlock <= level && !this.cookies.getVip) {
                if (item.type === SkinType.FIGHTER) {
                    return !fighterSet.includes(item.id);
                }
                if (item.type === SkinType.BUILDING) {
                    return !buildingSet.includes(item.id);
                }
            }

            return false;
        });
        return items.length ? <GiftType>Random.from(items) : {id: 0, type: "money"};
    }
    public updateSkins(): void {
        this.clearSkins();
        this.fillSkins();
    }

    public get selectedColorSet(): ISelectableColor | undefined {
        return SelectableColorsDataSet.filter(
            item => item.id === this.cookies.selected_color_set_id
        ).map(item => ({
            ...item,
            selected: true,
            stored: true
        }))[0];
    }
    public getBuildingTextureBy(type: PlayerType): string {
        return this.buildings.get(type)!;
    }

    public getFighterTextureBy(type: PlayerType): string {
        return this.fighters.get(type)!;
    }

    public getColorBy(type: PlayerType): PlayerColor {
        let result: PlayerColor = GameColors.players[type];
        if (type === PlayerType.First) {
            if (this.selectedColorSet?.data) {
                result = this.selectedColorSet.data;
            }
        } else if (type === PlayerType.Second) {
            if (this.selectedColorSet?.id) {
                let colorData = SelectableColorsDataSet.filter(
                    item => item.id === UserSelectableColorsSet.BLUE
                );

                if (this.selectedColorSet.id === UserSelectableColorsSet.BLUE) {
                    colorData = SelectableColorsDataSet.filter(
                        item => item.id === UserSelectableColorsSet.RED
                    );
                }

                if (colorData.length) {
                    result = colorData[0].data;
                }
            }
        }

        return result;
    }

    public get availableBuildings(): ISelectableBuilding[] {
        return this.selectFrom(
            SelectableBuildingDataSet,
            this.cookies.getUserBuildingSet(),
            this.cookies.selected_building_id
        );
    }

    public get availableFighters(): ISelectableFighter[] {
        return this.selectFrom(
            SelectableFighterDataSet,
            this.cookies.getUserFighterSet(),
            this.cookies.selected_fighter_id
        );
    }
    private selectFrom<T extends {id: number; enemyLevelUnlock: number; playerLevelUnlock: number}>(
        items: T[],
        selectedIds: number[],
        selectedId: number
    ): (T & {selected: boolean; stored: boolean})[] {
        return items
            .filter(
                item =>
                    this.cookies.absoluteLevelNum > item.playerLevelUnlock ||
                    selectedIds.indexOf(item.id) !== -1
            )
            .map(item => {
                const selected = item.id === selectedId;
                const stored = selectedIds.indexOf(item.id) !== -1;
                return {...item, selected, stored};
            });
    }
}
